import {
  useCallback, useEffect, useRef, useState,
  type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject,
} from 'react';
import Icon from './Icon';
import {
  ALLOWED_COLSPANS, ALLOWED_ROWSPANS, GRID_COLS, PANEL_LABEL,
  isPinned, moveBy, reorder, setSize, showing, stepSpan, togglePin,
  type Layout, type PanelId,
} from '../insights-layout';

/**
 * THE INSIGHTS GRID — Style B, one uniform snap grid (approved #269).
 *
 * Every shown panel is one card on a 12-column grid, each card locked to whole
 * column-spans and row-units so they pack edge-to-edge and top-to-bottom with no
 * dead space. While arranging, each card carries an 18px drag grip and a pin in
 * its header and a 16px resize handle in its corner. Dragging a card floats it
 * under the cursor, a dashed brand-tinted ghost slot shows where it will land, and
 * the rest reflow to close up around it — except a PINNED card, which holds its
 * cell while the shuffle flows past it.
 *
 * Reorder and resize both have a keyboard path (arrow keys on the focused grip and
 * handle), so the whole thing is usable without a mouse.
 *
 * NOTHING HERE TOUCHES THE PANELS THEMSELVES — they are dropped in as `nodeFor`,
 * the same components the screen has always rendered. The grid only places, sizes,
 * reorders and pins them. Hidden panels are still rendered (kept out of view) so a
 * panel turned off does not vanish from the document.
 */

type Mode = 'wide' | 'medium' | 'narrow';

/**
 * How many of the 12 columns a card takes at the current width. Wide keeps the
 * card's stored span; medium (a laptop) bumps only the narrow tiles (span 3) to a
 * two-up half and leaves the wider spans alone, so an 8+4 pair still fills a row to
 * 12 instead of the 8 going full and its 4-partner sitting alone with an empty half
 * (the dead space the ux review caught); narrow (a phone) stacks everything one-up.
 * This is the ~1080 / ~720 responsive feel the rest of the app already has.
 */
function effectiveCols(span: number, mode: Mode): number {
  if (mode === 'narrow') return GRID_COLS;
  if (mode === 'medium') return span <= 3 ? GRID_COLS / 2 : span;
  return span;
}

/** Snap a raw fractional span onto the nearest allowed whole span. */
function snap(values: readonly number[], raw: number): number {
  return values.reduce((best, cur) => (Math.abs(cur - raw) < Math.abs(best - raw) ? cur : best), values[0]);
}

interface DragState {
  id: PanelId;
  offsetX: number; offsetY: number;   // cursor position within the grabbed card
  x: number; y: number;               // current cursor position
  width: number; height: number;      // the floating card's size
  beforeId: PanelId | null;           // where the ghost slot sits (null = at the end)
}

interface ResizeState {
  id: PanelId;
  colSpan: number; rowSpan: number;   // the live preview size
}

export default function InsightsGrid({
  layout, arranging, onChange, nodeFor, gridRef,
}: {
  layout: Layout;
  arranging: boolean;
  onChange: (l: Layout) => void;
  nodeFor: Record<PanelId, ReactNode>;
  gridRef: RefObject<HTMLDivElement>;
}) {
  const [mode, setMode] = useState<Mode>('wide');
  const [drag, setDrag] = useState<DragState | null>(null);
  const [resize, setResize] = useState<ResizeState | null>(null);
  const cells = useRef(new Map<PanelId, HTMLDivElement>());

  // The width the cards see is the grid's own, not the window's — a collapsed
  // sidebar or a split view changes it without the window moving.
  useEffect(() => {
    const el = gridRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const read = () => {
      const w = el.clientWidth;
      setMode(w >= 960 ? 'wide' : w >= 620 ? 'medium' : 'narrow');
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [gridRef]);

  const visible = layout.order.filter((id) => showing(layout, id));

  /* Which shown card the cursor is in front of — the ghost slot's home. Read in
     the grid's own reading order: a card is "before" the cursor if it sits on an
     earlier row, or on the same row and to the left. */
  const dropTarget = useCallback((x: number, y: number, dragId: PanelId): PanelId | null => {
    let before: PanelId | null = null;
    for (const id of visible) {
      if (id === dragId) continue;
      const el = cells.current.get(id);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      const midX = r.left + r.width / 2;
      const onEarlierRow = y < r.top;
      const sameRow = y >= r.top && y <= r.bottom;
      if (onEarlierRow || (sameRow && x < midX)) { before = id; break; }
    }
    return before;
  }, [visible]);

  // ── DRAG ──────────────────────────────────────────────────────────────────
  const startDrag = (id: PanelId) => (e: ReactPointerEvent) => {
    if (isPinned(layout, id) || e.button !== 0) return;
    const el = cells.current.get(id);
    if (!el) return;
    const r = el.getBoundingClientRect();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({
      id,
      offsetX: e.clientX - r.left, offsetY: e.clientY - r.top,
      x: e.clientX, y: e.clientY, width: r.width, height: r.height,
      beforeId: id,
    });
    e.preventDefault();
  };
  const onDragMove = (e: ReactPointerEvent) => {
    if (!drag) return;
    setDrag({ ...drag, x: e.clientX, y: e.clientY, beforeId: dropTarget(e.clientX, e.clientY, drag.id) });
  };
  const endDrag = (e: ReactPointerEvent) => {
    if (!drag) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    // reorder no-ops when the ghost never left the card's own slot (beforeId === id).
    onChange(reorder(layout, drag.id, drag.beforeId));
    setDrag(null);
  };
  const gripKey = (id: PanelId) => (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { onChange(moveBy(layout, id, -1)); e.preventDefault(); }
    else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { onChange(moveBy(layout, id, 1)); e.preventDefault(); }
  };

  // ── RESIZE ────────────────────────────────────────────────────────────────
  const startResize = (id: PanelId) => (e: ReactPointerEvent) => {
    if (isPinned(layout, id) || e.button !== 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const s = layout.sizes[id];
    setResize({ id, colSpan: s.colSpan, rowSpan: s.rowSpan });
    e.preventDefault();
    e.stopPropagation();
  };
  const onResizeMove = (e: ReactPointerEvent) => {
    if (!resize) return;
    const grid = gridRef.current;
    const cell = cells.current.get(resize.id);
    if (!grid || !cell) return;
    const gr = grid.getBoundingClientRect();
    const cs = getComputedStyle(grid);
    const gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
    const rowUnit = parseFloat(cs.gridAutoRows || '180') || 180;
    const colUnit = (gr.width - gap * (GRID_COLS - 1)) / GRID_COLS + gap;
    const left = cell.getBoundingClientRect().left;
    const top = cell.getBoundingClientRect().top;
    const rawCols = (e.clientX - left + gap) / colUnit;
    const rawRows = (e.clientY - top + gap) / (rowUnit + gap);
    const colSpan = snap(ALLOWED_COLSPANS, Math.max(1, rawCols));
    const rowSpan = snap(ALLOWED_ROWSPANS, Math.max(1, rawRows));
    if (colSpan !== resize.colSpan || rowSpan !== resize.rowSpan) setResize({ id: resize.id, colSpan, rowSpan });
  };
  const endResize = (e: ReactPointerEvent) => {
    if (!resize) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    onChange(setSize(layout, resize.id, { colSpan: resize.colSpan, rowSpan: resize.rowSpan }));
    setResize(null);
  };
  const resizeKey = (id: PanelId) => (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') { onChange(stepSpan(layout, id, 'col', 1)); e.preventDefault(); }
    else if (e.key === 'ArrowLeft') { onChange(stepSpan(layout, id, 'col', -1)); e.preventDefault(); }
    else if (e.key === 'ArrowDown') { onChange(stepSpan(layout, id, 'row', 1)); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { onChange(stepSpan(layout, id, 'row', -1)); e.preventDefault(); }
  };

  const spanStyle = (id: PanelId): CSSProperties => {
    const size = resize?.id === id ? resize : layout.sizes[id];
    return {
      gridColumn: `span ${effectiveCols(size.colSpan, mode)}`,
      gridRow: `span ${size.rowSpan}`,
    };
  };

  const renderGhost = () => {
    if (!drag) return null;
    const size = layout.sizes[drag.id];
    return (
      <div
        key="ins-ghost"
        className="ins-ghost"
        aria-hidden="true"
        style={{ gridColumn: `span ${effectiveCols(size.colSpan, mode)}`, gridRow: `span ${size.rowSpan}` }}
      >
        <span className="ins-ghost-say">Drop here</span>
      </div>
    );
  };

  const cell = (id: PanelId) => {
    const shown = showing(layout, id);
    const pinned = isPinned(layout, id);
    const dragging = drag?.id === id;
    const label = PANEL_LABEL[id];
    const floating: CSSProperties | undefined = dragging
      ? {
          position: 'fixed', zIndex: 60, pointerEvents: 'none',
          width: drag!.width, height: drag!.height,
          left: drag!.x - drag!.offsetX, top: drag!.y - drag!.offsetY,
        }
      : undefined;
    return (
      <div
        key={id}
        ref={(el) => { if (el) cells.current.set(id, el); else cells.current.delete(id); }}
        className={`ins-cell${pinned ? ' is-pinned' : ''}${dragging ? ' is-dragging' : ''}${arranging ? ' is-arranging' : ''}`}
        data-panel={id}
        hidden={!shown}
        style={floating ?? spanStyle(id)}
      >
        {arranging && shown && (
          <div className="ins-tools">
            {pinned ? (
              <span className="ins-grip is-locked" aria-hidden="true" title={`${label} is pinned — unpin to move it`}>
                <Icon name="grip" size={18} />
              </span>
            ) : (
              <button
                type="button"
                className="ins-grip"
                aria-label={`Reorder ${label}. Use the arrow keys to move it, or drag it.`}
                onPointerDown={startDrag(id)}
                onPointerMove={onDragMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onKeyDown={gripKey(id)}
              >
                <Icon name="grip" size={18} />
              </button>
            )}
          </div>
        )}

        {/* The pin state, top-right. Pinned shows the calm filled "Pinned" chip
            (which unpins while arranging); an unpinned card shows the outline pin
            only while arranging. */}
        {pinned ? (
          arranging ? (
            <button type="button" className="ins-pin is-pinned" aria-label={`Unpin ${label}`} onClick={() => onChange(togglePin(layout, id))}>
              <Icon name="pin-filled" size={14} /> Pinned
            </button>
          ) : (
            shown && <span className="ins-pin is-pinned is-static"><Icon name="pin-filled" size={14} /> Pinned</span>
          )
        ) : (
          arranging && shown && (
            <button type="button" className="ins-pin" aria-label={`Pin ${label} in place`} onClick={() => onChange(togglePin(layout, id))}>
              <Icon name="pin" size={16} />
            </button>
          )
        )}

        {nodeFor[id]}

        {arranging && shown && !pinned && (
          <button
            type="button"
            className="ins-resize"
            aria-label={`Resize ${label}. Left and right change the width; up and down change the height.`}
            onPointerDown={startResize(id)}
            onPointerMove={onResizeMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
            onKeyDown={resizeKey(id)}
          >
            <Icon name="resize" size={16} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      ref={gridRef}
      className={`ins-grid${arranging ? ' is-arranging' : ''}${drag ? ' is-dragging' : ''}`}
    >
      {layout.order.map((id) =>
        drag && drag.beforeId === id && drag.beforeId !== drag.id && showing(layout, id)
          ? [renderGhost(), cell(id)]
          : cell(id)
      )}
      {drag && drag.beforeId === null && renderGhost()}
    </div>
  );
}
